/**
 * Feedback de quem usa o sistema, lido pelo suporte da Cliqfy (#561).
 *
 * Qualquer pessoa logada envia, de qualquer tela, pelo botão flutuante. Quem
 * lê é só a organização de suporte, numa tela própria do menu. O envio é um
 * formulário (texto e até cinco imagens); a tela, a pessoa e a hora são
 * preenchidas sozinhas, e não pelo que a pessoa digita.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

export const LIMITES_DO_FEEDBACK = {
  caracteres: 5000,
  imagens: 5,
  /** Por imagem. Um print de tela inteira cabe; um vídeo não. */
  megabytesPorImagem: 2,
} as const;

/**
 * Os campos de texto do formulário. As imagens vão no campo `imagens` do mesmo
 * formulário, e o tipo de cada uma é conferido pelos primeiros bytes.
 */
export const EnviarFeedbackSchema = z
  .object({
    texto: z.string().trim().min(1).max(LIMITES_DO_FEEDBACK.caracteres),
    /** O caminho da tela de onde a pessoa abriu o feedback, sem query string. */
    tela_caminho: z.string().trim().min(1).max(500),
    /** O nome da tela como o menu mostra ("Funil"), quando a tela sabe. */
    tela_nome: z.string().trim().max(120).optional(),
    versao_do_front: z.string().trim().max(80).optional(),
  })
  .strict();

export type EnviarFeedback = z.infer<typeof EnviarFeedbackSchema>;

export const FeedbackEnviadoSchema = z.object({
  id: IdSchema,
  criado_em: InstanteSchema,
});

export type FeedbackEnviado = z.infer<typeof FeedbackEnviadoSchema>;

export const FeedbackDoSuporteSchema = z.object({
  id: IdSchema,
  criado_em: InstanteSchema,
  /** A organização de quem escreveu, com o nome do momento do envio. */
  organizacao: z.object({ nome: z.string() }),
  /** Quem escreveu, como era no envio: o suporte responde pelo e-mail. */
  autor: z.object({ nome: z.string(), email: z.string().nullable() }),
  tela: z.object({ caminho: z.string(), nome: z.string().nullable() }),
  texto: z.string(),
  /** Links assinados, válidos por cinco minutos: recarregar a lista renova. */
  imagens: z.array(z.object({ url: z.string(), tipo: z.string() })),
});

export type FeedbackDoSuporte = z.infer<typeof FeedbackDoSuporteSchema>;

export const FeedbacksDoSuporteSchema = z.object({
  itens: z.array(FeedbackDoSuporteSchema),
  /** Quantos existem ao todo; a lista traz os mais recentes, até `LIMITE_DA_LISTA`. */
  total: z.number().int().nonnegative(),
});

export type FeedbacksDoSuporte = z.infer<typeof FeedbacksDoSuporteSchema>;

/** Quantos a tela do suporte lista de uma vez, do mais novo para o mais antigo. */
export const LIMITE_DA_LISTA_DE_FEEDBACKS = 200;
